// frontend/src/components/portfolio/BlockLibrary.tsx
import React from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Tooltip
} from '@mui/material';
import {
  Image as ImageIcon,
  Flag as FlagIcon,
  BarChart as BarChartIcon,
  Work as WorkIcon,
  CardGiftcard as CardGiftcardIcon,
  PhotoLibrary as PhotoLibraryIcon,
  FormatQuote as FormatQuoteIcon,
  TextFields as TextFieldsIcon,
  Email as EmailIcon,
  Group as GroupIcon,
  Event as EventIcon,
  Timeline as TimelineIcon,
  People as PeopleIcon,
  Campaign as CampaignIcon,
  Visibility as VisibilityIcon,
  PlayCircle as PlayCircleIcon,
  Link as LinkIcon,
  AccountTree as AccountTreeIcon,
  LibraryMusic as ArtistSearchIcon
} from '@mui/icons-material';
import { BLOCK_REGISTRY, getCategories } from '../../utils/blockRegistry';

interface BlockLibraryProps {
  onAddBlock: (type: string) => void;
}

const iconMap: Record<string, React.ReactElement> = {
  hero: <ImageIcon />,
  mission_vision: <FlagIcon />,
  metrics: <BarChartIcon />,
  case_studies: <WorkIcon />,
  offerings: <CardGiftcardIcon />,
  gallery: <PhotoLibraryIcon />,
  testimonials: <FormatQuoteIcon />,
  rich_text: <TextFieldsIcon />,
  contact_form: <EmailIcon />,
  team: <GroupIcon />,
  upcoming_performances: <EventIcon />,
  past_impact: <TimelineIcon />,
  audience_demographics: <PeopleIcon />,
  media_reach: <CampaignIcon />,
  brand_visibility: <VisibilityIcon />,
  video_showcase: <PlayCircleIcon />,
  links: <LinkIcon />,
  programme_initiatives: <AccountTreeIcon />,
  artist: <ArtistSearchIcon />

};

const categoryNames: Record<string, string> = {
  foundation: 'Foundation',
  impact: 'Impact',
  programs: 'Programs',
  media: 'Media',
  engagement: 'Engagement',
  financial: 'Financial'
};

const BlockLibrary: React.FC<BlockLibraryProps> = ({ onAddBlock }) => {
  const categories = getCategories();

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ p: 2, borderBottom: '1px solid #3A9188' }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5, color: '#ffffff' }}>
          Add Blocks
        </Typography>
        <Typography variant="caption" sx={{ color: '#A4B0BE' }}>
          Click to add to canvas
        </Typography>
      </Box>

      {/* Block List */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {categories.map((category) => {
          const blocksInCategory = BLOCK_REGISTRY.filter(
            (block) => block.category === category
          );

          if (blocksInCategory.length === 0) return null;

          return (
            <Box key={category}>
              {/* Category Header */}
              <Box sx={{ px: 2, py: 1, bgcolor: '#2C3E50' }}>
                <Typography
                  variant="overline"
                  sx={{
                    fontWeight: 700,
                    color: '#3A9188',
                    letterSpacing: 1
                  }}
                >
                  {categoryNames[category] || category}
                </Typography>
              </Box>

              {/* Blocks in Category */}
              <List dense disablePadding>
                {blocksInCategory.map((block) => (
                  <Tooltip
                    key={block.type}
                    title={block.description}
                    placement="right"
                    arrow
                  >
                    <ListItem disablePadding>
                      <ListItemButton
                        onClick={() => onAddBlock(block.type)}
                        sx={{
                          py: 1.5,
                          '&:hover': {
                            bgcolor: 'rgba(58, 145, 136, 0.1)'
                          }
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 40, color: '#3A9188' }}>
                          {iconMap[block.type] || <ImageIcon />}
                        </ListItemIcon>
                        <ListItemText
                          primary={block.label}
                          primaryTypographyProps={{
                            variant: 'body2',
                            fontWeight: 500,
                            color: '#ffffff'
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  </Tooltip>
                ))}
              </List>

              <Divider sx={{ borderColor: '#3A9188' }} />
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default BlockLibrary;